import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, LifeBuoy, Paperclip, CheckCircle2 } from "lucide-react";

export default function Support() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("email", email);
      fd.append("subject", subject);
      fd.append("message", message);
      if (file) fd.append("file", file);
      await api.post("/feedback", fd);
      setSent(true);
      toast.success("Thanks — your message has been sent to our team.");
      setSubject(""); setMessage(""); setFile(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="support-page">
      <div className="flex items-center gap-2 mb-1.5">
        <LifeBuoy className="h-6 w-6 text-[#1a3060]" strokeWidth={1.75} />
        <h1 className="font-heading text-2xl font-bold tracking-tight">Feedback &amp; support</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Share feedback or report an issue. Your message goes straight to our support team and we'll reply to the email you enter below.</p>

      {sent && (
        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 mb-5" data-testid="support-success">
          <CheckCircle2 className="h-4 w-4 shrink-0" /> Message sent — thank you. You can send another anytime.
        </div>
      )}

      <form onSubmit={submit} className="space-y-4" data-testid="support-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Name</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="support-name" className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Email</Label>
            <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="support-email" className="h-11" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wider">Subject</Label>
          <Input required value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="How can we help?" data-testid="support-subject" className="h-11" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wider">Message</Label>
          <Textarea required value={message} onChange={(e) => setMessage(e.target.value)} rows={6} placeholder="Tell us what happened, what you expected, and any steps to reproduce it." data-testid="support-message" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wider">Attachment (optional)</Label>
          <label className="flex items-center gap-2 text-sm border border-dashed border-zinc-300 rounded-lg px-3 py-2.5 cursor-pointer hover:border-[#1a3060] transition-colors" data-testid="support-file-label">
            <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground truncate">{file ? file.name : "Upload a screenshot or file (max 5MB)"}</span>
            <input type="file" accept="image/*,.pdf,.doc,.docx,.txt" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="support-file" />
          </label>
        </div>
        <Button type="submit" disabled={loading} data-testid="support-submit" className="w-full h-11 active:scale-[0.98] transition-transform">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send message"}
        </Button>
      </form>
    </div>
  );
}
